import { useState, useCallback, useEffect } from "react";
import { BoundaryPoint, ProjectData } from "../../types";
import {
  findClosestLineSegment,
  insertBoundaryPoint,
  createBoundaryPoint,
} from "../utils/boundaryUtils";
import type { MarkerDragEvent } from "react-map-gl/mapbox";

interface UseBoundaryLogicProps {
  projectData: ProjectData | null;
  setProjectData: (data: ProjectData | null) => void;
}

export const useBoundaryLogic = ({
  projectData,
  setProjectData,
}: UseBoundaryLogicProps) => {
  const [boundaryPoints, setBoundaryPoints] = useState<BoundaryPoint[]>([]);
  const [isBoundaryMode, setIsBoundaryMode] = useState<boolean>(false);
  const [selectedBoundaryPoint, setSelectedBoundaryPoint] =
    useState<BoundaryPoint | null>(null);

  useEffect(() => {
    if (projectData?.boundaryPoints) {
      setBoundaryPoints(projectData.boundaryPoints);
    }
  }, [projectData]);

  // Wrapper function to re-number boundary points before setting them
  const setBoundaryPointsWithRenumbering = useCallback(
    (points: BoundaryPoint[]) => {
      const renumberedPoints = points.map((point, index) => ({
        ...point,
        order: index,
      }));
      setBoundaryPoints(renumberedPoints);
    },
    []
  );

  const handleBoundaryModeToggle = useCallback(
    (operation: string) => {
      if (operation === "start") {
        setIsBoundaryMode(true);

        let boundary = projectData?.boundaryPoints || [];

        if (boundary.length === 0 && projectData?.location) {
          const lng_offset = 0.0001;
          const lat_offset = 0.0001;

          const locations = [
            {
              lng: projectData.location.longitude + lng_offset,
              lat: projectData.location.latitude - lat_offset,
            },
            {
              lng: projectData.location.longitude - lng_offset,
              lat: projectData.location.latitude - lat_offset,
            },
            {
              lng: projectData.location.longitude - lng_offset,
              lat: projectData.location.latitude + lat_offset,
            },
            {
              lng: projectData.location.longitude + lng_offset,
              lat: projectData.location.latitude + lat_offset,
            },
          ];

          locations.forEach((point, index) => {
            const newBoundaryPoint = createBoundaryPoint(
              point.lng,
              point.lat,
              index
            );
            boundary = insertBoundaryPoint(boundary, newBoundaryPoint, index);
          });
          // Initialize editing state with saved boundary points
        }

        setBoundaryPointsWithRenumbering(boundary);
      } else if (operation === "cancel") {
        setIsBoundaryMode(false);
        // Reset to saved boundary points when canceling
        setBoundaryPointsWithRenumbering(projectData?.boundaryPoints || []);
      } else if (operation === "clear") {
        // Clear boundary points but don't save
        setBoundaryPointsWithRenumbering([]);
      } else if (operation === "save") {
        console.log("LATEST BOUNDARY POINTS: ", boundaryPoints);
        // Update project data with new boundary points
        if (projectData) {
          const updatedProjectData = {
            ...projectData,
            boundaryPoints: boundaryPoints,
          };
          setProjectData(updatedProjectData);
        }
        setIsBoundaryMode(false);
      }
    },
    [projectData, setProjectData, boundaryPoints]
  );

  const handleBoundaryClick = useCallback(
    (lng: number, lat: number) => {
      if (!isBoundaryMode) return;

      // Check if click is near an existing boundary line (within 10 meters)
      if (boundaryPoints.length >= 2) {
        const { index: closestSegmentIndex, distance: minDistance } =
          findClosestLineSegment(lng, lat, boundaryPoints);

        // If click is close to a line segment, insert a new point
        if (minDistance < 0.00001) {
          // Roughly 1 meter in degrees
          const newBoundaryPoint = createBoundaryPoint(
            lng,
            lat,
            closestSegmentIndex + 1
          );
          const updatedBoundaryPoints = insertBoundaryPoint(
            boundaryPoints,
            newBoundaryPoint,
            closestSegmentIndex + 1
          );

          setBoundaryPointsWithRenumbering(updatedBoundaryPoints);

          return; // Exit early since we handled the line click
        }
      }

      // If not clicking on a line, add a new boundary point at the end
      const newBoundaryPoint = createBoundaryPoint(
        lng,
        lat,
        boundaryPoints.length
      );
      const updatedBoundaryPoints = [...boundaryPoints, newBoundaryPoint];

      setBoundaryPointsWithRenumbering(updatedBoundaryPoints);
    },
    [
      isBoundaryMode,
      boundaryPoints,
      projectData,
      setProjectData,
      setBoundaryPointsWithRenumbering,
    ]
  );

  const handleBoundaryMarkerDragEnd = useCallback(
    (event: MarkerDragEvent, pointId: string) => {
      if (!projectData) return;

      const { lng, lat } = event.lngLat;

      // Update the boundary points with the new coordinates
      const updatedBoundaryPoints = boundaryPoints.map((point) =>
        point.id === pointId
          ? { ...point, coordinates: { longitude: lng, latitude: lat } }
          : point
      );

      setBoundaryPointsWithRenumbering(updatedBoundaryPoints);
    },
    [
      projectData,
      boundaryPoints,
      setProjectData,
      setBoundaryPointsWithRenumbering,
    ]
  );

  const handleBoundaryMarkerClick = (event: any, pointId: string) => {
    // Stop event propagation to prevent handleMapClick from firing
    if (event.originalEvent) {
      event.originalEvent.stopPropagation();
    }

    // TODO: Add boundary point editing functionality in the Panel
    console.log("Boundary point clicked:", pointId);
  };

  return {
    boundaryPoints,
    setBoundaryPoints: setBoundaryPointsWithRenumbering, // Return the wrapper function instead of the original
    isBoundaryMode,
    selectedBoundaryPoint,
    setSelectedBoundaryPoint,
    handleBoundaryModeToggle,
    handleBoundaryClick,
    handleBoundaryMarkerDragEnd,
    handleBoundaryMarkerClick,
  };
};
